/**
 * The CEO Overview palette and scale — Card P145.
 *
 * Lifted from the mockup Daniel approved (28 Oct) rather than re-invented, so
 * the built screen and the design agree on every hex. Kept in one module
 * because a palette copied into six components is six places to drift.
 */
/*
 * ⛔⛔ CARD P198/P199 — THE SOFT-UI PALETTE, AND THREE TOKENS DARKENED.
 *
 * Neumorphism's known weakness is contrast: the card stops being white, so
 * every text ratio drops. MEASURED on the new surfaces — `muted` fell to
 * 3.98:1 and `watch` to 4.13:1, both below the 4.5:1 AA floor, on a screen
 * Carlos reads daily. `faint` was already below AA on WHITE (2.54:1) and is
 * fixed here rather than inherited.
 *
 * Final ratios against the DARKER of the two surfaces (the ground), so each
 * token is safe on a card and on the page:
 *
 *   text  #374151  8.47:1      body  #4B5563  6.21:1
 *   muted #565D6B  5.44:1      faint #5B6370  4.99:1
 *   high  #B91C1C  5.32:1      watch #92400E  5.83:1
 *   primary #1D4ED8 5.51:1
 *
 * The ladder body > muted > faint is preserved, so the type hierarchy still
 * reads — darkening them all to the same value would have met AA and flattened
 * the page.
 *
 * ⚠⚠ `faint` AND `watch` ARE ALSO CHART FILLS (CeoOverview's donut and the
 * unassigned bar). Darkening them for TEXT necessarily moves those two fills.
 * That is reported in the PR for W1's P196 rather than worked around, because
 * the alternative — leaving them — means a row COUNT and the axis ticks stay
 * below AA, which is the thing this card was asked to fix.
 *
 * ⛔ STATUS MEANING IS UNCHANGED. `watch` is still amber (it is the palette's
 * own `watchDeep`), `high` is still the same red, and deltaTone() is untouched.
 */
/**
 * ⭐⭐ THE CATEGORICAL SERIES PALETTE — Okabe-Ito, on WHITE WELLS.
 * Cards P220 (finding) and P222 (resolution).
 *
 * ─── WHY THE FILLS ARE THE PUBLISHED HEXES AGAIN ───────────────────────────
 * P220 measured that five of seven Okabe-Ito colours fall below WCAG 1.4.11's
 * 3:1 on the soft-grey page ground (#E6E9EF) — orange at 1.85:1. Darkening
 * them fixed the background and BROKE the palette: Okabe-Ito separates blue
 * from sky by LIGHTNESS, both sitting at ~202° of hue, so darkening collapsed
 * them to 1.38:1 with 0° between them.
 *
 * ⭐ P222's resolution, which is the standard one: charts sit in a WHITE WELL
 * inside the soft card, and 1.4.11 is met through the BOUNDARY rather than the
 * fill. On white the published hues measure 2.25–5.19:1 as fills, and every
 * outline below clears 3:1 comfortably.
 *
 * ─── ⛔ COLOUR IS NEVER THE ONLY SIGNAL (WCAG 1.4.1) ───────────────────────
 * Every series also carries a legend entry and, where space allows, a direct
 * data label. A reader who cannot separate two hues reads the label.
 *
 * ⚠ FILL CONTRAST AGAINST THE WELL IS NOT THE TEST, and P220 got this wrong
 * before P222 corrected it: a luminance ratio between two series measures the
 * wrong thing, because categorical series are told apart by HUE — and by
 * lightness where Okabe-Ito intends it (blue vs sky, 2.25:1). The controls in
 * tests/unit/security/softUiContrast.test.js check the OUTLINE against the
 * well, label text for AA, and hue-or-lightness separation between fills.
 */
const OKABE_ITO = Object.freeze({
  blue: "#0072B2", orange: "#E69F00", greenTeal: "#009E73",
  pink: "#CC79A7", sky: "#56B4E9", vermillion: "#D55E00",
});

/** FILLS — the published hues, unmodified. */
const SERIES = Object.freeze({
  seriesDirect: OKABE_ITO.blue,
  seriesAffiliate: OKABE_ITO.orange,
  series3: OKABE_ITO.greenTeal,
  series4: OKABE_ITO.pink,
  series5: OKABE_ITO.sky,
  series6: OKABE_ITO.vermillion,
  /** ⚠ "Other"/unclassified ONLY — never a category in its own right. */
  seriesOther: "#80858A",
});

/**
 * OUTLINES — each fill's own hue scaled to 72% toward black: visibly darker
 * than its fill (1.61–1.85:1) and ≥3:1 against the white well (4.18–8.34:1).
 * ⚠ Derived from the hue, not picked, so a fill change cannot leave an
 * outline behind that no longer belongs to it.
 */
const SERIES_INK = Object.freeze({
  seriesDirectInk: "#005280",
  seriesAffiliateInk: "#A67200",
  series3Ink: "#007253",
  series4Ink: "#935778",
  series5Ink: "#3E82A8",
  series6Ink: "#994400",
  seriesOtherInk: "#5C6063",
});

/** Ordered fills, for a chart assigning colours to an unknown list. */
export const SERIES_ORDER = Object.freeze([
  SERIES.seriesDirect, SERIES.seriesAffiliate, SERIES.series3,
  SERIES.series4, SERIES.series5, SERIES.series6,
]);
/** The outline for each, in the same order. */
export const SERIES_INK_ORDER = Object.freeze([
  SERIES_INK.seriesDirectInk, SERIES_INK.seriesAffiliateInk, SERIES_INK.series3Ink,
  SERIES_INK.series4Ink, SERIES_INK.series5Ink, SERIES_INK.series6Ink,
]);
/** ⚠ Beyond SERIES_ORDER everything collapses here, never a 7th hue. */
export const SERIES_OTHER = SERIES.seriesOther;
export const SERIES_OTHER_INK = SERIES_INK.seriesOtherInk;
/** ⛔ P222 item 4 — at most six categorical series; the tail is "Other". */
export const MAX_SERIES = 6;
/** The canonical hexes, for audit. */
export const OKABE_ITO_SOURCE = OKABE_ITO;

/**
 * ⚠ FORECAST IS THE SAME HUE, NOT A NEW COLOUR (P222 item 6): reduced opacity
 * plus a dashed outline. A separate hue would read as another category.
 */
export const FORECAST_FILL_OPACITY = 0.4;
export const FORECAST_DASH = "4 3";

export const C = Object.freeze({
  ...SERIES,
  ...SERIES_INK,
  /* ⭐ P222 item 1 — charts sit on WHITE inside the soft card. */
  well: "#FFFFFF",
  ground: "#E6E9EF",
  card: "#EEF1F5",
  border: "#E3E7EE",
  borderSoft: "#EEF1F5",
  rule: "#D9DEE7",
  text: "#374151",
  body: "#4B5563",
  muted: "#565D6B",
  faint: "#5B6370",
  primary: "#1D4ED8",
  primaryDeep: "#1E3A8A",
  high: "#B91C1C",
  highDeep: "#7F1D1D",
  highBg: "#FEF2F2",
  watch: "#92400E",
  watchDeep: "#92400E",
});

export const FONT = "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif";

/**
 * ⚠ Up is not always good. Overdue receivables rising is bad; collected
 * rising is good. A delta chip that always paints "up" green would be
 * confidently wrong on half the cards, so direction is a property of the
 * CARD, never of the arithmetic.
 */
export function deltaTone(pct, higherIsBetter = true) {
  if (pct === null || pct === undefined || !Number.isFinite(pct) || pct === 0) return C.muted;
  const good = pct > 0 ? higherIsBetter : !higherIsBetter;
  return good ? "#15803D" : C.high;
}

export const S = Object.freeze({
  page: { background: "var(--soft-ground, #F7F9FC)", fontFamily: FONT, color: C.body, padding: 20, minHeight: "100%" },
  row: { display: "grid", gap: 16, marginBottom: 16 },
  kpiRow: { gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" },
  twoUp: { gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))" },
  threeUp: { gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" },
  /*
   * ⭐ THE RAISED CARD. Depth comes from the dual shadow, not a border — a
   * border and a shadow together read as a sticker on a wall rather than an
   * extrusion of it.
   *
   * ⚠ `var(--soft-...)` IS READ FROM .ceo-soft, declared in ceoSoftUi.css.
   * An inline style can reference a custom property set on an ancestor, which
   * is what lets one stylesheet retune print and phone widths without a single
   * `!important` — and it degrades to the plain fallback anywhere the wrapper
   * class is absent, so nothing outside the CEO dashboard can inherit it.
   */
  card: {
    background: "var(--soft-card, #FFFFFF)",
    border: "none",
    borderRadius: "var(--soft-radius, 10px)",
    boxShadow: "var(--soft-raised, none)",
    padding: 18, minWidth: 0,
    transition: "box-shadow var(--soft-transition, 150ms) ease",
  },
  cardHead: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 12 },
  h2: { margin: 0, fontSize: 13, fontWeight: 700, color: C.text, letterSpacing: 0.2 },
  // ── Card P185: the custom-range control ──────────────────────────────
  /* ⚠ PRESSED, not raised — an input that looks raised invites a click and
     then does not move. */
  dateInput: { fontSize: 12, padding: '6px 9px', border: 'none',
               borderRadius: 'var(--soft-radius-sm, 6px)',
               background: 'var(--soft-card, #fff)',
               boxShadow: 'var(--soft-inset, none)', color: '#212529' },
  /* ⚠ A COLOURED button keeps its fill: the status/action colours carry
     meaning and must not dissolve into the surface. It gains the soft edge
     and the transition, nothing more. */
  applyBtn: { fontSize: 12, fontWeight: 600, padding: '6px 14px', border: 'none',
              borderRadius: 'var(--soft-radius-sm, 6px)', background: '#2E8B98',
              color: '#fff', cursor: 'pointer',
              boxShadow: 'var(--soft-raised-sm, none)',
              transition: 'box-shadow var(--soft-transition, 150ms) ease' },
  link: { fontSize: 12, color: C.primary, background: "none", border: "none", padding: 0, cursor: "pointer" },
  kpiLabel: { fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6 },
  kpiValue: { fontSize: 28, fontWeight: 700, color: C.text, lineHeight: 1.1, letterSpacing: -0.5 },
  kpiSub: { fontSize: 11, color: C.faint, marginTop: 6, lineHeight: 1.45 },
});
